<script setup lang="ts">
import type { PositionCellValue, PositionRow } from '@/components/finest-custom-position-list/types'
import FinestFieldInput from '@/components/FinestFieldInput.vue'
import FinestInputNumber from '@/components/FinestInputNumber.vue'
import type { EnumOption } from '@/composables/useEnumOptions'
import type { FieldInputProps } from '@/utils/field-value'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import LoanCollateralList from './LoanCollateralList.vue'
import {
  hasAnnuity,
  loanSections,
  monthlyAnnuity,
  repaymentRateFromYearlyAnnuity,
  yearlyAnnuity,
  type LoanField,
} from './loanFields'
import { cellNumber } from './loanFigures'
import CollateralData = App.Data.EstateObject.CollateralData
import FinancingType = App.Enums.EstateObject.Loans.FinancingType

const props = defineProps<{
  row: PositionRow
  estateObjectId: string
  loanTypeOptions: EnumOption[]
  mezzanineTypeOptions: EnumOption[]
  collateralTypeOptions: EnumOption[]
  collaterals: CollateralData[]
  ensureLoanId: () => Promise<string>
}>()

const emit = defineEmits<{
  update: [columnKey: string, value: PositionCellValue]
  collateralsChange: [collaterals: CollateralData[]]
  remove: []
  commit: []
}>()

const { t } = useI18n({ useScope: 'local' })

const loanType = computed(() => (props.row.cells.loan_type as FinancingType | null) ?? null)

const sections = computed(() => loanSections(loanType.value))

const showsAnnuity = computed(() => hasAnnuity(loanType.value))

const principal = computed(() => cellNumber(props.row, 'principal_amount'))
const interestRate = computed(() => cellNumber(props.row, 'nominal_interest_rate'))
const repaymentRate = computed(() => cellNumber(props.row, 'initial_repayment_rate'))

const monthlyRate = computed(() => monthlyAnnuity(principal.value, interestRate.value, repaymentRate.value))
const yearlyRate = computed(() => yearlyAnnuity(principal.value, interestRate.value, repaymentRate.value))

const selectOptions = (field: LoanField): EnumOption[] =>
  field.key === 'mezzanine_type' ? props.mezzanineTypeOptions : props.loanTypeOptions

const inputProps = (field: LoanField): FieldInputProps => {
  const label = t(`fields.${field.key}`)

  switch (field.kind) {
    case 'select':
      return { type: 'select', label, options: selectOptions(field) }
    case 'percent':
      return {
        type: 'percent',
        label,
        suffix: field.key === 'nominal_interest_rate' ? ' % p.a.' : ' %',
        placeholder: field.key === 'nominal_interest_rate' ? t('unit.percentPerYear') : t('unit.percent'),
        fractionDigits: 2,
      }
    case 'years':
      return { type: 'number', label, suffix: ` ${t('unit.years')}`, placeholder: t('unit.years'), fractionDigits: 0 }
    case 'text':
      return { type: 'text', label, placeholder: field.key === 'lender_name' ? t('lenderPlaceholder') : '' }
    default:
      return { type: field.kind, label }
  }
}

const updateRepaymentRateFromAnnuity = (yearly: number | null): void => {
  emit('update', 'initial_repayment_rate', repaymentRateFromYearlyAnnuity(principal.value, interestRate.value, yearly))
}
</script>

<template>
  <div>
    <div class="flex flex-col gap-5">
      <section v-for="section in sections" :key="section.key">
        <h4 class="section-label section-label--sm mb-2">
          {{ t(`sections.${section.key}`) }}
        </h4>

        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div v-for="field in section.fields" :key="field.key" :class="field.kind === 'text' ? 'sm:col-span-2' : ''">
            <label class="field-label">{{ t(`fields.${field.key}`) }}</label>

            <FinestFieldInput
              v-bind="inputProps(field)"
              :model-value="row.cells[field.key] ?? null"
              @update:model-value="(value) => emit('update', field.key, value)"
              @commit="emit('commit')" />
          </div>

          <template v-if="section.key === 'interest' && showsAnnuity">
            <div>
              <label class="field-label">{{ t('fields.monthly_annuity') }}</label>
              <FinestInputNumber
                :model-value="monthlyRate"
                mode="currency"
                :aria-label="t('fields.monthly_annuity')"
                @update:model-value="(value) => updateRepaymentRateFromAnnuity(value === null ? null : value * 12)" />
            </div>

            <div>
              <label class="field-label">{{ t('fields.yearly_annuity') }}</label>
              <FinestInputNumber
                :model-value="yearlyRate"
                mode="currency"
                :aria-label="t('fields.yearly_annuity')"
                @update:model-value="updateRepaymentRateFromAnnuity" />
            </div>

            <p class="text-ink-3 text-xs sm:col-span-2">{{ t('annuityHint') }}</p>
          </template>
        </div>
      </section>

      <LoanCollateralList
        :key="row.key"
        :estate-object-id="estateObjectId"
        :loan-id="row.id ?? null"
        :ensure-loan-id="ensureLoanId"
        :initial-collaterals="collaterals"
        :collateral-type-options="collateralTypeOptions"
        @change="(list) => emit('collateralsChange', list)" />

      <div class="border-line-2 flex justify-end border-t pt-4">
        <button
          type="button"
          class="text-danger hover:bg-danger-subtle inline-flex cursor-pointer items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium"
          @click="emit('remove')">
          <i class="pi pi-trash text-[13px]" aria-hidden="true" />
          {{ t('remove') }}
        </button>
      </div>
    </div>
  </div>
</template>

<i18n lang="json">
{
  "de": {
    "lenderPlaceholder": "z. B. Sparkasse Rhein-Neckar-Nord",
    "remove": "Entfernen",
    "annuityHint": "Annuität folgt aus Zins und Tilgung – überschreibt du sie, passt sich der Tilgungssatz an.",
    "unit": {
      "years": "J.",
      "percent": "%",
      "percentPerYear": "% p.a."
    },
    "sections": {
      "basics": "Grunddaten",
      "amount": "Betrag & Sicherheit",
      "schedule": "Laufzeit",
      "interest": "Zins & Tilgung",
      "mezzanine": "Mezzanine"
    },
    "fields": {
      "lender_name": "Darlehensgeber",
      "loan_type": "Finanzierungsart",
      "principal_amount": "Anfänglicher Darlehensbetrag",
      "mortgage": "Grundschulden auf dem Objekt",
      "final_payment_amount": "Schlussrate",
      "start_date": "Darlehensbeginn",
      "repayment_start_date": "Tilgungsbeginn",
      "exit_date": "Exit-Datum",
      "fixed_interest_rate_term": "Zinsbindung",
      "loan_term": "Darlehenslaufzeit",
      "nominal_interest_rate": "Zinssatz",
      "initial_repayment_rate": "Tilgungssatz",
      "monthly_annuity": "mon. Annuität",
      "yearly_annuity": "p.a. Annuität",
      "mezzanine_type": "Mezzanine-Art"
    }
  },
  "en": {
    "lenderPlaceholder": "e.g. Sparkasse Rhein-Neckar-Nord",
    "remove": "Remove",
    "annuityHint": "The annuity follows from interest and repayment rate – overriding it adjusts the repayment rate.",
    "unit": {
      "years": "yrs",
      "percent": "%",
      "percentPerYear": "% p.a."
    },
    "sections": {
      "basics": "Basics",
      "amount": "Amount & security",
      "schedule": "Term",
      "interest": "Interest & repayment",
      "mezzanine": "Mezzanine"
    },
    "fields": {
      "lender_name": "Lender",
      "loan_type": "Financing type",
      "principal_amount": "Initial loan amount",
      "mortgage": "Land charges on the property",
      "final_payment_amount": "Final payment",
      "start_date": "Loan start",
      "repayment_start_date": "Repayment start",
      "exit_date": "Exit date",
      "fixed_interest_rate_term": "Fixed interest term",
      "loan_term": "Loan term",
      "nominal_interest_rate": "Interest rate",
      "initial_repayment_rate": "Repayment rate",
      "monthly_annuity": "Annuity / month",
      "yearly_annuity": "Annuity / year",
      "mezzanine_type": "Mezzanine type"
    }
  }
}
</i18n>
